import { isApolloError, useMutation } from '@apollo/client';
import Alert from '@risk-smart/themed-cloudscape-components/alert';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Link from '@risksmart-app/components/src/link';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import {
  DeleteEntityDocument,
  InsertEntityDocument,
  UpdateEntityDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import DeleteButton from 'src/components/delete-button';
import DeleteModal from 'src/components/delete-modal';
import { ModalForm } from 'src/components/form/form/ModalForm';
import { useGetEntities } from 'src/hooks/queries/entity/useGetEntities';
import { useGetEntityById } from 'src/hooks/queries/entity/useGetEntityById';
import type { RiskRegisterFields } from 'src/pages/risks/types';
import { getOwners } from 'src/rbac/contributorHelper';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import { UserOrGroupsSchema } from 'src/schemas/global';
import { evictField } from 'src/utils/graphqlUtils';
import { riskRegisterUrl } from 'src/utils/urls';
import { z } from 'zod';

import EntityFormFields from './EntityFormFields';

interface Props {
  Id?: string;
  isVisible: boolean;
  onDismiss: () => void;
}

const schema = z.object({
  Name: z.string(),
  ParentId: z.string().nullish(),
  Description: z.string().nullish(),
  Weight: z.coerce.number().positive().default(1.0),
  Owners: UserOrGroupsSchema.min(1, { message: 'Required' }),
});

type SchemaFields = z.infer<typeof schema>;

const defaultValues = {
  Name: '',
  Description: '',
  ParentId: null,
  Weight: 1.0,
  Owners: [],
};

interface DeleteBlockedState {
  hasLinkedRisks: boolean;
  hasChildEntities: boolean;
}

const EntityDetailsModal: FC<Props> = ({ onDismiss, Id, isVisible }) => {
  const { t } = useTranslation(['common']);
  const { t: et } = useTranslation(['common'], { keyPrefix: 'entity' });
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState<DeleteBlockedState | null>(
    null
  );

  const { data } = useGetEntityById({
    queryArgs: { id: Id },
    shouldSkip: !Id,
  });
  const {
    data: entitiesData,
    loading: entitiesLoading,
    refetch: entitiesRefetch,
  } = useGetEntities({ queryArgs: {} });

  const { hasPermission: canDelete } = useHasPermissionQuery('delete:entity');

  const parentOptions = useMemo(
    () =>
      entitiesData?.entity
        .filter((entity) => entity.Id !== Id)
        .map((entity) => ({
          value: entity.Id,
          label: entity.Name,
        })),
    [entitiesData, Id]
  );

  const [insert] = useMutation(InsertEntityDocument, {
    update: (cache) => {
      evictField(cache, 'entity');
      evictField(cache, 'entity_by_pk');
    },
  });
  const [update] = useMutation(UpdateEntityDocument, {
    update: (cache) => {
      evictField(cache, 'entity');
      evictField(cache, 'entity_by_pk');
    },
  });
  const [deleteEntity, { loading: deleteLoading }] = useMutation(
    DeleteEntityDocument,
    {
      update: (cache) => {
        evictField(cache, 'entity');
        evictField(cache, 'entity_by_pk');
      },
    }
  );

  const isEditMode = !!Id;

  const handleDeleteClick = () => {
    setDeleteBlocked(null);
    setIsDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    try {
      await deleteEntity({
        variables: { Id: Id! },
      });
      setIsDeleteModalVisible(false);
      await entitiesRefetch();
      onDismiss();
    } catch (error) {
      setIsDeleteModalVisible(false);

      // isApolloError expects Error; catch block types error as unknown
      if (error instanceof Error && isApolloError(error)) {
        const extensions = error.graphQLErrors[0]?.extensions as
          | {
              code?: string;
              linkedRiskCount?: number;
              childEntityCount?: number;
            }
          | undefined;

        if (extensions?.code === 'ENTITY_DELETE_BLOCKED') {
          setDeleteBlocked({
            hasLinkedRisks: (extensions.linkedRiskCount ?? 0) > 0,
            hasChildEntities: (extensions.childEntityCount ?? 0) > 0,
          });

          return;
        }
      }

      addNotification({
        type: 'error',
        content: et('delete_error'),
      });
    }
  };

  const onSave = async (data: SchemaFields) => {
    if (Id) {
      await update({
        variables: {
          Id,
          ...data,
          owners: data.Owners.filter((c) => c.type === 'user').map(
            (c) => c.value
          ),
          ownerGroups: data.Owners.filter((c) => c.type === 'userGroup').map(
            (c) => c.value
          ),
        },
      });
    } else {
      await insert({
        variables: {
          ...data,
          owners: data.Owners.filter((c) => c.type === 'user').map(
            (c) => c.value
          ),
          ownerGroups: data.Owners.filter((c) => c.type === 'userGroup').map(
            (c) => c.value
          ),
        },
      });
    }
    await entitiesRefetch();
    onDismiss();
  };

  const handleDismiss = () => {
    setDeleteBlocked(null);
    setIsDeleteModalVisible(false);
    onDismiss();
  };

  const entityName = data?.entity_by_pk?.Name;

  const getRiskRegisterFilteredUrl = () =>
    riskRegisterUrl({
      tokens: [
        {
          // 'Entity' is a valid RiskRegisterFields key; Extract narrows to string keys as required by TypedPropertyFilterToken
          propertyKey: 'Entity' as Extract<keyof RiskRegisterFields, string>,
          operator: '=',
          value: entityName ?? '',
        },
      ],
      operation: 'and',
    });

  if (entitiesLoading) {
    return <div>{'Loading...'}</div>;
  }

  return (
    <>
      <ModalForm<SchemaFields>
        onDismiss={handleDismiss}
        visible={isVisible}
        defaultValues={defaultValues}
        values={
          data?.entity_by_pk
            ? {
                ...data.entity_by_pk,
                Owners: getOwners(data.entity_by_pk),
              }
            : defaultValues
        }
        formId={'entityDetailsForm'}
        schema={schema}
        onSave={onSave}
        i18n={t('entity')}
        footerDetails={
          isEditMode && canDelete ? (
            <DeleteButton
              onClick={handleDeleteClick}
              data-testid={'entity-delete-button'}
            >
              {t('delete')}
            </DeleteButton>
          ) : undefined
        }
      >
        <SpaceBetween size={'m'}>
          <EntityFormFields entities={parentOptions} />

          {deleteBlocked && (
            <Alert type={'warning'} data-testid={'delete-blocked-alert'}>
              <SpaceBetween size={'xs'}>
                {deleteBlocked.hasLinkedRisks && (
                  <div>
                    {et('delete_blocked_risks')}{' '}
                    <Link
                      variant={'primary'}
                      onFollow={() => {
                        handleDismiss();
                        navigate(getRiskRegisterFilteredUrl());
                      }}
                    >
                      {et('view_risks_link')}
                    </Link>
                  </div>
                )}
                {deleteBlocked.hasChildEntities && (
                  <div>{et('delete_blocked_children')}</div>
                )}
              </SpaceBetween>
            </Alert>
          )}
        </SpaceBetween>
      </ModalForm>

      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={et('delete')}
        onDelete={handleDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {et('confirm_delete_message')}
      </DeleteModal>
    </>
  );
};

export default EntityDetailsModal;
